import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Tag, PackageCheck, MessageSquare, Building2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { formatPrice } from "@/components/ListingCard";

function notifLink(n) {
  if (n.link) return n.link;
  if (n.type === "support_message") {
    return n.channel === "supplier" ? "/support-matpass" : "/admin?tab=support";
  }
  if (n.type === "moderation_pending") return n.link || "/admin?tab=ai_moderation";
  if (n.type === "saved_search") return n.link || "/mes-recherches";
  if (n.conversation_id && (n.type === "purchase_request" || n.type === "purchase_accepted" || n.type === "purchase_refused")) {
    return `/messages/${n.conversation_id}`;
  }
  if (n.listing_id) return `/annonce/${n.listing_id}`;
  return "/favoris";
}

function notifTitle(n) {
  if (n.type === "support_message") return n.title || "Message MatPass";
  if (n.type === "saved_search") return n.title || "Nouvelle alerte";
  if (n.type === "price_drop") return "Baisse de prix";
  if (n.type === "sold") return "Annonce vendue";
  if (n.type === "purchase_request") return "Demande d'achat";
  if (n.type === "purchase_accepted") return "Réservation acceptée";
  if (n.type === "purchase_refused") return "Demande refusée";
  if (n.type === "reservation_cancelled") return "Réservation annulée";
  if (n.type === "listing_available_again") return "Machine remise en vente";
  if (n.type === "moderation_pending") return n.title || "Annonce à vérifier";
  return n.title || "Notification";
}

function notifBody(n) {
  if (n.type === "support_message") return n.body || "";
  if (n.type === "supplier_request" || n.type === "supplier_approved" || n.type === "supplier_rejected" || n.type === "supplier_as_user") {
    return n.body || "";
  }
  if (n.type === "moderation_pending") return n.body || "Annonce suspecte détectée par la modération automatique.";
  if (n.type === "saved_search") {
    return n.message || n.listing_title || "De nouvelles annonces correspondent à votre alerte.";
  }
  if (n.type === "purchase_request") {
    return n.message || "Nouvelle demande d'achat.";
  }
  if (n.type === "purchase_accepted") {
    return n.message || "Réservation acceptée.";
  }
  if (n.type === "purchase_refused") {
    return n.message || n.listing_title || "Annonce";
  }
  if (n.type === "reservation_cancelled") {
    return n.message || "La réservation a été annulée.";
  }
  if (n.type === "listing_available_again") {
    return n.message || "Bonne nouvelle ! La machine qui vous intéressait est de nouveau disponible.";
  }
  if (n.type === "sold") {
    return n.message || n.listing_title || "Cette machine a été vendue.";
  }
  if (n.type === "price_drop") {
    return (
      <>
        {n.listing_title || "Annonce"}
        {n.new_price != null && (
          <> · <span className="text-matpro-green font-bold">{formatPrice(n.new_price)}</span> (au lieu de <s>{formatPrice(n.old_price)}</s>)</>
        )}
      </>
    );
  }
  return n.body || n.listing_title || n.message || "";
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const rootRef = useRef(null);

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.unread || 0);
    } catch {}
  };

  const loadList = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch {}
  };

  useEffect(() => {
    loadUnread();
    const iv = setInterval(loadUnread, 20000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!open) return;
    loadList();
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const openPanel = async () => {
    setOpen(true);
    if (unread > 0) {
      try { await api.post("/notifications/read-all"); } catch {}
      setUnread(0);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={openPanel}
        data-testid="nav-notifications"
        className="relative h-9 w-9 rounded-md flex items-center justify-center text-matpro-anthracite hover:text-matpro-green hover:bg-matpro-green-light transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-matpro-green text-white text-[10px] font-bold h-4 min-w-4 px-1">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-matpro-border rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-matpro-border flex items-center justify-between">
            <div className="font-heading font-bold text-matpro-anthracite text-sm">Notifications</div>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-matpro-anthracite-muted">
              Aucune notification pour le moment.
            </div>
          ) : (
            <ul className="divide-y divide-matpro-border">
              {items.map((n) => (
                <li key={n.id}>
                  <Link
                    to={notifLink(n)}
                    onClick={() => setOpen(false)}
                    className="flex gap-3 p-3 hover:bg-matpro-bg transition-colors"
                    data-testid={`notif-${n.id}`}
                  >
                    <div className="h-8 w-8 flex-shrink-0 rounded-md bg-matpro-green-light text-matpro-green flex items-center justify-center">
                      {n.type === "support_message" ? (
                        <MessageSquare className="h-4 w-4" />
                      ) : n.type === "supplier_request" || n.type === "supplier_approved" || n.type === "supplier_rejected" || n.type === "supplier_as_user" ? (
                        <Building2 className="h-4 w-4" />
                      ) : n.type === "price_drop" ? (
                        <Tag className="h-4 w-4" />
                      ) : n.type === "saved_search" ? (
                        <Bell className="h-4 w-4" />
                      ) : n.type === "moderation_pending" ? (
                        <AlertTriangle className="h-4 w-4" />
                      ) : (
                        <PackageCheck className="h-4 w-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-matpro-anthracite truncate">
                        {notifTitle(n)}
                      </div>
                      <div className="text-xs text-matpro-anthracite-muted truncate">
                        {notifBody(n)}
                      </div>
                      {n.created_at && (
                        <div className="text-[10px] text-matpro-anthracite-muted mt-0.5">
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: fr })}
                        </div>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
